import { SolColor, SolTag, SolTagTextStyle, SolText } from '@sol/components';
import { SolIcon } from '@sol/icons';

/**
 * Display model for a priority level.
 */
export interface PriorityDisplay {
  text: string;
  tagColor: SolColor;
}

/** Priority level display table and the mappings into it. */
export class AsmPriorityTools {
  private constructor() {}

  public static readonly PRIORITY_LEVELS: PriorityDisplay[] = [
    { text: 'Critical', tagColor: 'danger' },
    { text: 'Very High', tagColor: 'red' },
    { text: 'High', tagColor: 'tangerine' },
    { text: 'Medium', tagColor: 'warning' },
    { text: 'Low', tagColor: 'yellow' },
    { text: 'Very Low', tagColor: 'green' },
    { text: 'OK', tagColor: 'success' },
  ];

  /**
   * Map a priority float (0-1) to its priority display.
   */
  public static priorityToDisplay(priority: number): PriorityDisplay {
    if (priority >= 1.0) return AsmPriorityTools.PRIORITY_LEVELS[0];
    if (priority >= 0.8) return AsmPriorityTools.PRIORITY_LEVELS[1];
    if (priority >= 0.6) return AsmPriorityTools.PRIORITY_LEVELS[2];
    if (priority >= 0.4) return AsmPriorityTools.PRIORITY_LEVELS[3];
    if (priority >= 0.2) return AsmPriorityTools.PRIORITY_LEVELS[4];
    if (priority > 0.0) return AsmPriorityTools.PRIORITY_LEVELS[5];
    return AsmPriorityTools.PRIORITY_LEVELS[6];
  }

  /**
   * Map a priority float (0-1) to a SolTag for display in tables and detail views.
   */
  public static priorityToTag(priority: number): SolTag {
    return AsmPriorityTools.toTag(AsmPriorityTools.priorityToDisplay(priority));
  }

  /**
   * Map a priority keyword string ("Critical", "High", etc.) to a SolTag for display.
   * Used by the data source view derivation (backend returns string keywords).
   */
  public static keywordToTag(keyword: string | undefined | null): SolTag {
    const display =
      AsmPriorityTools.PRIORITY_LEVELS.find(p => p.text.toLowerCase() === (keyword ?? '').toLowerCase()) ??
      AsmPriorityTools.PRIORITY_LEVELS[6]; // default to OK
    return AsmPriorityTools.toTag(display);
  }

  /** The graded-level dot tag shared by Priority, Suspicious and the certificate protocols. */
  public static levelTag(text: SolText, color: SolColor, textStyle: SolTagTextStyle = 'bold'): SolTag {
    return SolTag.of(text)
      .withTextColor('ink')
      .withTextStyle(textStyle)
      .withIcon(SolIcon.circle_filled_small)
      .withIconColor(color);
  }

  private static toTag(display: PriorityDisplay): SolTag {
    return AsmPriorityTools.levelTag(SolText.ofRaw(display.text), display.tagColor);
  }
}
